import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, LogOut, CalendarCheck, Clock3, CheckCircle2, XCircle } from "lucide-react";
import SitePanel from "@/pages/admin/SitePanel";
import ServicesPanel from "@/pages/admin/ServicesPanel";
import TeamPanel from "@/pages/admin/TeamPanel";
import GalleryPanel from "@/pages/admin/GalleryPanel";
import PlanningPanel from "@/pages/admin/PlanningPanel";
import SchedulePanel from "@/pages/admin/SchedulePanel";
import DepositsPanel from "@/pages/admin/DepositsPanel";

const TABS = [
  { key: "rdv", label: "Rendez-vous" },
  { key: "planning", label: "Planning" },
  { key: "horaires", label: "Horaires" },
  { key: "acomptes", label: "Acomptes" },
  { key: "prestations", label: "Prestations" },
  { key: "equipe", label: "Équipe" },
  { key: "galerie", label: "Galerie" },
  { key: "site", label: "Site & style" },
];

const STATUS_LABELS = { pending: "En attente", confirmed: "Confirmé", cancelled: "Annulé" };
const STATUS_STYLES = {
  pending: "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]",
  confirmed: "bg-[#336633]/10 text-[#336633]",
  cancelled: "bg-[#993333]/10 text-[#993333]",
};
const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "pending", label: "En attente" },
  { key: "confirmed", label: "Confirmés" },
  { key: "cancelled", label: "Annulés" },
];

function frDate(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [tab, setTab] = useState("rdv");

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    setLoadingBookings(true);
    api.get("/admin/bookings")
      .then((res) => setBookings(res.data.bookings))
      .catch(() => toast.error("Impossible de charger les rendez-vous."))
      .finally(() => setLoadingBookings(false));
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await api.post("/auth/login", loginForm);
      setUser(res.data);
      toast.success("Bienvenue Armina !");
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const setStatus = async (booking, status) => {
    try {
      const res = await api.patch(`/admin/bookings/${booking.id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === booking.id ? res.data.booking : b)));
      toast.success(status === "confirmed" ? "Rendez-vous confirmé — la cliente est prévenue par email." : "Rendez-vous annulé — la cliente est prévenue par email.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = useMemo(
    () => (filter === "all" ? bookings : bookings.filter((b) => b.status === filter)),
    [bookings, filter]
  );

  const stats = useMemo(() => ({
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
  }), [bookings]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--brand-section)]" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-[var(--brand-accent)]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--brand-section)] px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md border border-black/5 bg-[var(--brand-bg)] p-10 lg:p-14">
          <p className="font-display text-3xl text-[var(--brand-dark)]">Beauty <span className="italic font-light text-[var(--brand-accent)]">by</span> Armina</p>
          <p className="mt-2 text-xs uppercase tracking-[0.25em] text-[var(--brand-muted)]">Espace professionnel</p>
          <form onSubmit={login} className="mt-10 grid gap-5" data-testid="admin-login-form">
            <Input type="email" required placeholder="Email" value={loginForm.email}
                   onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                   className="h-14 rounded-none border-black/10 bg-transparent font-light" data-testid="admin-email-input" />
            <Input type="password" required placeholder="Mot de passe" value={loginForm.password}
                   onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                   className="h-14 rounded-none border-black/10 bg-transparent font-light" data-testid="admin-password-input" />
            {loginError && <p className="text-sm text-[#993333]" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loggingIn}
                    className="mt-2 rounded-full bg-[var(--brand-dark)] py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] transition-transform duration-300 hover:scale-[0.98] disabled:opacity-50"
                    data-testid="admin-login-submit">
              {loggingIn ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          <Link to="/" className="mt-8 inline-block text-xs uppercase tracking-[0.25em] text-[var(--brand-muted)] transition-colors hover:text-[var(--brand-accent)]" data-testid="admin-back-home">
            ← Retour au site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--brand-section)]" data-testid="admin-dashboard">
      <header className="border-b border-black/5 bg-[var(--brand-bg)]/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
          <p className="font-display text-2xl text-[var(--brand-dark)]" data-testid="admin-logo">
            Beauty <span className="italic font-light text-[var(--brand-accent)]">by</span> Armina
            <span className="ml-4 align-middle font-sans text-[10px] uppercase tracking-[0.3em] text-[var(--brand-muted)]">Espace pro</span>
          </p>
          <div className="flex items-center gap-6">
            <Link to="/" className="hidden text-xs uppercase tracking-[0.25em] text-[var(--brand-muted)] transition-colors hover:text-[var(--brand-accent)] sm:block" data-testid="admin-view-site">
              Voir le site
            </Link>
            <button onClick={logout} className="flex items-center gap-2 rounded-full border border-[var(--brand-dark)]/20 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-[var(--brand-dark)] transition-colors duration-300 hover:bg-[var(--brand-dark)] hover:text-[var(--brand-bg)]" data-testid="admin-logout-btn">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12 lg:px-12">
        <div className="flex flex-wrap gap-3" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`rounded-full border px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                      tab === t.key ? "border-[var(--brand-dark)] bg-[var(--brand-dark)] text-[var(--brand-bg)]" : "border-black/15 text-[var(--brand-dark)] hover:border-[var(--brand-accent)]"
                    }`}
                    data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "rdv" && (
          <>
        <h1 className="mt-10 font-display text-4xl text-[var(--brand-dark)] sm:text-5xl">Rendez-vous</h1>

        <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="admin-stats">
          {[
            { icon: CalendarCheck, label: "Total", value: stats.total, testid: "stat-total" },
            { icon: Clock3, label: "En attente", value: stats.pending, testid: "stat-pending" },
            { icon: CheckCircle2, label: "Confirmés", value: stats.confirmed, testid: "stat-confirmed" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-5 border border-black/5 bg-[var(--brand-bg)] p-6" data-testid={s.testid}>
              <s.icon size={22} strokeWidth={1.5} className="text-[var(--brand-accent)]" />
              <div>
                <p className="font-display text-3xl text-[var(--brand-dark)]">{s.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3" data-testid="admin-filters">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
                    className={`rounded-full border px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                      filter === f.key ? "border-[var(--brand-dark)] bg-[var(--brand-dark)] text-[var(--brand-bg)]" : "border-black/15 text-[var(--brand-dark)] hover:border-[var(--brand-accent)]"
                    }`}
                    data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto border border-black/5 bg-[var(--brand-bg)]" data-testid="admin-bookings-table">
          {loadingBookings ? (
            <div className="flex items-center justify-center gap-3 p-16 text-sm font-light text-[var(--brand-muted)]">
              <Loader2 size={18} className="animate-spin" /> Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <p className="p-16 text-center text-sm font-light italic text-[var(--brand-muted)]" data-testid="admin-empty-msg">
              Aucun rendez-vous pour ce filtre.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-black/10">
                  <TableHead className="text-xs uppercase tracking-[0.2em]">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.2em]">Heure</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.2em]">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.2em]">Prestation</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.2em]">Prix</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.2em]">Statut</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-[0.2em]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id} className="border-black/5" data-testid={`booking-row-${b.id}`}>
                    <TableCell className="font-light">{frDate(b.date)}</TableCell>
                    <TableCell className="font-light">{b.time}</TableCell>
                    <TableCell>
                      <p className="font-medium text-[var(--brand-dark)]">{b.client_name}</p>
                      <p className="text-xs font-light text-[var(--brand-muted)]">{b.email} · {b.phone}</p>
                      {b.message && <p className="mt-1 max-w-[220px] truncate text-xs italic text-[var(--brand-accent)]" title={b.message}>« {b.message} »</p>}
                    </TableCell>
                    <TableCell className="font-light">
                      {b.service_name}
                      {b.staff_name && <p className="mt-0.5 text-xs text-[var(--brand-muted)]">avec {b.staff_name}</p>}
                    </TableCell>
                    <TableCell className="font-display text-lg">{b.price} €</TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.15em] ${STATUS_STYLES[b.status]}`} data-testid={`booking-status-${b.id}`}>
                        {STATUS_LABELS[b.status]}
                      </span>
                      {(b.deposit_amount || 0) > 0 && (
                        <span className={`mt-1.5 inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${b.payment_status === "paid" ? "bg-[#336633]/10 text-[#336633]" : b.payment_status === "refunded" ? "bg-black/10 text-[var(--brand-muted)]" : "bg-[#993333]/10 text-[#993333]"}`} data-testid={`booking-payment-${b.id}`}>
                          {b.payment_status === "paid" ? `Acompte payé · ${b.deposit_amount} €` : b.payment_status === "refunded" ? "Remboursé" : `Acompte ${b.deposit_amount} € non payé`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {b.status === "pending" && (
                          <button onClick={() => setStatus(b, "confirmed")}
                                  className="flex items-center gap-1.5 rounded-full bg-[var(--brand-dark)] px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-95"
                                  data-testid={`confirm-btn-${b.id}`}>
                            <CheckCircle2 size={13} /> Confirmer
                          </button>
                        )}
                        {b.status !== "cancelled" && (
                          <button onClick={() => setStatus(b, "cancelled")}
                                  className="flex items-center gap-1.5 rounded-full border border-[#993333]/30 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[#993333] transition-colors duration-200 hover:bg-[#993333] hover:text-[var(--brand-bg)]"
                                  data-testid={`cancel-btn-${b.id}`}>
                            <XCircle size={13} /> Annuler
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
          </>
        )}

        {tab === "planning" && <PlanningPanel />}
        {tab === "horaires" && <SchedulePanel />}
        {tab === "acomptes" && <DepositsPanel />}
        {tab === "prestations" && <ServicesPanel />}
        {tab === "equipe" && <TeamPanel />}
        {tab === "galerie" && <GalleryPanel />}
        {tab === "site" && <SitePanel />}
      </main>
    </div>
  );
}
